/**
 * Detalle de una **tarea** de mantenimiento con su ciclo completo.
 *
 * Puerto de `views/maintenance/tasks/view.php`: el estado, el cierre con las
 * lecturas del contador, la aprobación (sólo administradores) y las evidencias.
 */

import {
  Ban,
  CalendarClock,
  CheckCircle2,
  Download,
  FileText,
  Gauge,
  Paperclip,
  PlayCircle,
  Trash2,
  Upload,
} from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { ArchivoInput } from '@/components/ui/archivo-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { descargarEvidencia } from '../tareas.api';
import {
  useAprobarTarea,
  useCambiarEstadoTarea,
  useEvidenciasTarea,
  useRechazarTarea,
  useTareaDetalle,
} from '../tareas.api';
import type { EstadoTarea, PrioridadTarea } from '../tareas.types';

/** Color de la barra de consumo según el estado del sub-activo. */
const BARRA: Record<string, string> = {
  danger: 'bg-rose-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  neutral: 'bg-ink-300',
};

/** Variante del badge según la prioridad (como el panel anterior). */
function variantePrioridad(prioridad: PrioridadTarea): 'danger' | 'warning' | 'neutral' {
  if (prioridad === 'CRÍTICA') return 'danger';
  if (prioridad === 'ALTA') return 'warning';

  return 'neutral';
}

/** Variante del badge según el estado de ejecución. */
function varianteEstado(estado: EstadoTarea): 'danger' | 'brand' | 'success' {
  if (estado === 'PENDIENTE') return 'danger';
  if (estado === 'EN_PROCESO') return 'brand';

  return 'success';
}

/** Tamaño legible de un archivo. */
function tamanioLegible(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Dato de la ficha de la tarea. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{etiqueta}</dt>
      <dd className="text-sm text-ink-800">{valor || '—'}</dd>
    </div>
  );
}

export function TareaDetalle({
  tareaId,
  esAdmin,
  onCerrar,
  onAviso,
}: {
  tareaId: number;
  esAdmin: boolean;
  onCerrar: () => void;
  onAviso: (mensaje: string) => void;
}) {
  const detalle = useTareaDetalle(tareaId);
  const cambiarEstado = useCambiarEstadoTarea();
  const aprobar = useAprobarTarea();
  const rechazar = useRechazarTarea();
  const { subir, eliminar } = useEvidenciasTarea(tareaId);

  const [cerrando, setCerrando] = useState(false);
  const [comentarios, setComentarios] = useState('');
  const [lecturaAntes, setLecturaAntes] = useState('');
  const [lecturaDespues, setLecturaDespues] = useState('');
  const [comentarioAprobacion, setComentarioAprobacion] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [rechazando, setRechazando] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [descripcionArchivo, setDescripcionArchivo] = useState('');
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const tarea = detalle.data?.tarea;
  const evidencias = detalle.data?.evidencias ?? [];
  const subActivo = detalle.data?.sub_activo ?? null;
  const mensajeDeError = (error: unknown) =>
    error instanceof ApiError || error instanceof Error
      ? error.message
      : 'La API no respondió.';

  /** Opciones comunes: avisan del éxito y muestran el error de la acción. */
  const comun = {
    onSuccess: (respuesta: { mensaje: string }) => {
      setErrorAccion(null);
      onAviso(respuesta.mensaje);
    },
    onError: (error: unknown) => setErrorAccion(mensajeDeError(error)),
  };

  return (
    <Dialog
      open
      onClose={onCerrar}
      size="xl"
      title={tarea ? tarea.tipo : 'Detalle de la tarea'}
      description={
        tarea ? `${tarea.activo} · ${tarea.sub_activo} · tarea #${tarea.id}` : 'Cargando la tarea…'
      }
      footer={
        tarea ? (
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={varianteEstado(tarea.estado)}>{tarea.estado_etiqueta}</Badge>
            <Badge variant={variantePrioridad(tarea.prioridad)}>{tarea.prioridad}</Badge>
            {tarea.aprobada ? <Badge variant="success">Aprobada</Badge> : null}
          </div>
        ) : null
      }
    >
      {detalle.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : detalle.error ? (
        <Alert variant="error" title="No se pudo leer la tarea">
          {mensajeDeError(detalle.error)}
        </Alert>
      ) : tarea ? (
        <div className="flex flex-col gap-5">
          {errorAccion ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {errorAccion}
            </Alert>
          ) : null}

          <dl className="grid gap-4 sm:grid-cols-3">
            <Dato etiqueta="Categoría" valor={tarea.categoria} />
            <Dato etiqueta="Programada" valor={tarea.programada ?? ''} />
            <Dato etiqueta="Ejecutada" valor={tarea.ejecucion ?? ''} />
            <Dato etiqueta="Creada por" valor={tarea.creado_por ?? ''} />
            <Dato etiqueta="Asignada a" valor={tarea.asignado_a ?? ''} />
            <Dato etiqueta="Ejecutada por" valor={tarea.ejecutado_por ?? ''} />
            <Dato
              etiqueta="Lecturas del contador"
              valor={
                tarea.lectura_antes === null && tarea.lectura_despues === null
                  ? ''
                  : `${tarea.lectura_antes === null ? '—' : formatNumber(tarea.lectura_antes)} → ${
                      tarea.lectura_despues === null ? '—' : formatNumber(tarea.lectura_despues)
                    }`
              }
            />
            <Dato etiqueta="Aprobada por" valor={tarea.aprobada_por ?? ''} />
            <Dato etiqueta="Fecha de aprobación" valor={tarea.aprobacion ?? ''} />
          </dl>

          {tarea.descripcion ? (
            <p className="rounded-card border border-ink-100 bg-ink-50/60 p-3 text-sm text-ink-600">
              {tarea.descripcion}
            </p>
          ) : null}

          {tarea.comentarios ? (
            <Alert variant="info" title="Comentarios del cierre">
              {tarea.comentarios}
            </Alert>
          ) : null}

          {tarea.comentario_aprobacion ? (
            <Alert variant={tarea.aprobada ? 'success' : 'warning'} title={tarea.aprobada ? 'Aprobación' : 'Motivo del rechazo'}>
              {tarea.comentario_aprobacion}
            </Alert>
          ) : null}

          {subActivo ? (
            <section className="flex flex-col gap-2">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                <Gauge className="size-4 text-brand-500" aria-hidden="true" />
                Consumo del sub-activo
              </h3>
              <div className="rounded-card border border-ink-100 p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-ink-700">
                    <strong>{subActivo.nombre}</strong> · {formatNumber(subActivo.usado)} de{' '}
                    {formatNumber(subActivo.umbral)} galones
                  </span>
                  <Badge variant={subActivo.estado_color}>{subActivo.estado_etiqueta}</Badge>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                  <div
                    className={cn('h-full rounded-full', BARRA[subActivo.estado_color] ?? 'bg-ink-300')}
                    style={{ width: `${subActivo.progreso}%` }}
                  />
                </div>
                {subActivo.ultimo_mantenimiento ? (
                  <p className="mt-2 flex items-center gap-1 text-[11px] text-ink-400">
                    <CalendarClock className="size-3" aria-hidden="true" />
                    Último mantenimiento: {subActivo.ultimo_mantenimiento}
                  </p>
                ) : null}
              </div>
            </section>
          ) : null}

          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-ink-900">Ciclo de la tarea</h3>

            <div className="flex flex-wrap items-center gap-2">
              {tarea.estado === 'PENDIENTE' ? (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<PlayCircle className="size-3.5" aria-hidden="true" />}
                  loading={cambiarEstado.isPending}
                  onClick={() =>
                    cambiarEstado.mutate(
                      { id: tarea.id, payload: { estado: 'EN_PROCESO' } },
                      comun,
                    )
                  }
                >
                  Iniciar tarea
                </Button>
              ) : null}

              {tarea.estado !== 'REALIZADO' ? (
                <Button
                  size="sm"
                  leftIcon={<CheckCircle2 className="size-3.5" aria-hidden="true" />}
                  onClick={() => setCerrando((abierto) => !abierto)}
                >
                  Marcar como realizada
                </Button>
              ) : null}
            </div>

            {cerrando && tarea.estado !== 'REALIZADO' ? (
              <div className="flex flex-col gap-3 rounded-card border border-ink-100 bg-ink-50/60 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input
                    label="Lectura antes"
                    type="number"
                    min={0}
                    step="0.01"
                    value={lecturaAntes}
                    hint="Totalizador al empezar"
                    onChange={(evento) => setLecturaAntes(evento.target.value)}
                  />
                  <Input
                    label="Lectura después"
                    type="number"
                    min={0}
                    step="0.01"
                    value={lecturaDespues}
                    hint="Si es un cambio de filtro, reinicia el contador al aprobar"
                    onChange={(evento) => setLecturaDespues(evento.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="tarea-comentarios" className="text-sm font-medium text-ink-700">
                    Comentarios del cierre
                  </label>
                  <textarea
                    id="tarea-comentarios"
                    rows={2}
                    value={comentarios}
                    className="w-full rounded-control border border-ink-200 bg-white px-4 py-3 text-sm text-ink-900 outline-none transition-all duration-200 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
                    onChange={(evento) => setComentarios(evento.target.value)}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    loading={cambiarEstado.isPending}
                    onClick={() =>
                      cambiarEstado.mutate(
                        {
                          id: tarea.id,
                          payload: {
                            estado: 'REALIZADO',
                            comentarios,
                            lectura_antes: lecturaAntes,
                            lectura_despues: lecturaDespues,
                          },
                        },
                        comun,
                      )
                    }
                  >
                    Guardar y cerrar la tarea
                  </Button>
                </div>
              </div>
            ) : null}

            {tarea.estado === 'REALIZADO' && !tarea.aprobada ? (
              esAdmin ? (
                <div className="flex flex-col gap-3 rounded-card border border-ink-100 p-3">
                  <Input
                    label="Comentario de aprobación"
                    value={comentarioAprobacion}
                    hint="Opcional"
                    onChange={(evento) => setComentarioAprobacion(evento.target.value)}
                  />
                  <div className="flex flex-wrap items-end gap-2">
                    <Button
                      size="sm"
                      leftIcon={<CheckCircle2 className="size-3.5" aria-hidden="true" />}
                      loading={aprobar.isPending}
                      onClick={() =>
                        aprobar.mutate({ id: tarea.id, comentario: comentarioAprobacion }, comun)
                      }
                    >
                      Aprobar tarea
                    </Button>

                    {rechazando ? (
                      <div className="flex flex-1 items-end gap-2">
                        <Input
                          containerClassName="flex-1"
                          label="Motivo del rechazo"
                          value={motivoRechazo}
                          onChange={(evento) => setMotivoRechazo(evento.target.value)}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          loading={rechazar.isPending}
                          disabled={motivoRechazo.trim().length === 0}
                          onClick={() =>
                            rechazar.mutate({ id: tarea.id, motivo: motivoRechazo.trim() }, comun)
                          }
                        >
                          Confirmar rechazo
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                        onClick={() => setRechazando(true)}
                      >
                        Rechazar
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <Alert variant="info">
                  La tarea está realizada y espera la aprobación de un administrador.
                </Alert>
              )
            ) : null}
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Paperclip className="size-4 text-brand-500" aria-hidden="true" />
              Evidencias ({evidencias.length})
            </h3>

            {evidencias.length === 0 ? (
              <Alert variant="info">Todavía no hay fotos ni documentos adjuntos.</Alert>
            ) : (
              <ul className="flex flex-col gap-2">
                {evidencias.map((evidencia) => (
                  <li
                    key={evidencia.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-ink-100 p-2.5"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <FileText className="size-4 shrink-0 text-ink-400" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-ink-800">
                          {evidencia.nombre}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {tamanioLegible(evidencia.tamano)}
                          {evidencia.subida_por ? ` · ${evidencia.subida_por}` : ''}
                          {evidencia.descripcion ? ` · ${evidencia.descripcion}` : ''}
                        </span>
                      </span>
                    </span>

                    <span className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Descargar"
                        aria-label={`Descargar ${evidencia.nombre}`}
                        onClick={() => {
                          descargarEvidencia(evidencia).catch((error: unknown) =>
                            setErrorAccion(mensajeDeError(error)),
                          );
                        }}
                      >
                        <Download className="size-4" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Eliminar"
                        aria-label={`Eliminar ${evidencia.nombre}`}
                        loading={eliminar.isPending}
                        onClick={() => eliminar.mutate(evidencia.id, comun)}
                      >
                        <Trash2 className="size-4 text-rose-500" aria-hidden="true" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-end gap-2 rounded-card border border-dashed border-ink-200 p-3">
              <ArchivoInput
                className="min-w-56 flex-1"
                label="Adjuntar foto o PDF"
                accept="image/jpeg,image/png,image/gif,application/pdf"
                nombre={archivo?.name ?? null}
                placeholder="Sin evidencia seleccionada"
                onSeleccionar={(elegido) => setArchivo(elegido?.archivo ?? null)}
              />
              <Input
                containerClassName="min-w-40"
                label="Descripción"
                value={descripcionArchivo}
                onChange={(evento) => setDescripcionArchivo(evento.target.value)}
              />
              <Button
                size="sm"
                leftIcon={<Upload className="size-3.5" aria-hidden="true" />}
                disabled={!archivo}
                loading={subir.isPending}
                onClick={() => {
                  if (!archivo) return;
                  setErrorAccion(null);
                  subir.mutate(
                    { archivo, descripcion: descripcionArchivo },
                    {
                      onSuccess: () => {
                        setArchivo(null);
                        setDescripcionArchivo('');
                        setErrorAccion(null);
                        onAviso('Evidencia cargada.');
                      },
                      onError: (error: unknown) => setErrorAccion(mensajeDeError(error)),
                    },
                  );
                }}
              >
                Subir
              </Button>
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
